import { memo, useEffect, useMemo, useRef, type ComponentType, type ReactElement } from "react";

import classes from "./MasonryGrid.module.css";
import own from "./VirtualMasonry.module.css";
import { NO_LAYOUT, useGrid, useView } from "./virtual-view";

export interface Placed {
  index: number;
  top: number;
  height: number;
}

/**
 * Where each card sits: dealt to the columns in turn (item n to column n mod count, live's
 * `Masonry`), each column stacking its cards at their own heights (the column's width over the
 * card's shape), `gap` apart.
 */
export function placeCards(ratios: readonly number[], count: number, width: number, gap: number) {
  const lanes = Array.from({ length: count }, () => ({ height: 0, cards: [] as Placed[] }));
  ratios.forEach((ratio, index) => {
    const lane = lanes[index % count]!;
    const height = width / (ratio > 0 ? ratio : 1);
    const top = lane.cards.length === 0 ? 0 : lane.height + gap;
    lane.cards.push({ index, top, height });
    lane.height = top + height;
  });
  return lanes;
}

/** What a grid's card is given: its item, and what every card reads besides (`data`). */
export interface GridCardProps<T, D> {
  item: T;
  data: D;
}

export type CardType<T, D> = ComponentType<GridCardProps<T, D>>;

/** A mounted card, rendered again only when its item, `data` or card type changes, never on a
 *  scroll step or a new page (issue 794: every mounted card rendering again made the first
 *  scroll after a load 1,869 ms of long tasks). */
export const Slot = memo(function Slot<T, D>({ card: Card, item, data }: { card: CardType<T, D>; item: T; data: D }) {
  return <Card item={item} data={data} />;
}) as <T, D>(props: { card: CardType<T, D>; item: T; data: D }) => ReactElement;

export interface VirtualMasonryProps<T, D> {
  items: readonly T[];
  /** Stable (a module's function): the places are computed again only when it, the items or the
   *  width change. */
  keyOf: (item: T) => string;
  /** The card's width over its height: its shape, known before its picture loads. Stable, as
   *  `keyOf`. */
  ratioOf: (item: T) => number;
  /**
   * One card: a component of the module that shows the grid, never one made in a render, so it
   * reads nothing but its item and `data`. Keep each item the same object while it is unchanged.
   */
  card: CardType<T, D>;
  /** What every card reads besides its item (the screen's state and callbacks), memoized by the
   *  screen: a new value renders every mounted card again. */
  data: D;
  /** The column count: the page's persisted setting (`libraryColumns`). */
  columns: number;
  /** The element that scrolls the grid; null until it mounts. */
  root: HTMLElement | null;
  /** Names the gallery for a screen reader, as a group. */
  label?: string;
  gap?: number;
}

/**
 * The live app's masonry (`MasonryGrid`, design-system.md 3.8) for a long list (issue 794): the same
 * columns, dealing and rise, but only the cards within a screen of the view are mounted, each at
 * the place its shape gives it, so a page of hundreds (or a scroll through thousands) holds a few
 * dozen cards and their pictures. Every other card's place is a grey tile, live's skeleton, so a
 * fast scroll shows tiles rather than white while the cards mount. Until its place under the
 * view is first read (a frame after it mounts) no card is mounted: coming back to a list of
 * hundreds mounted them all for that frame (model-reviewer's 1.1 to 1.5 s). Where nothing can be
 * laid out (jsdom, a hidden scroller) every card is mounted in the flow, as `MasonryGrid`'s.
 */
export function VirtualMasonry<T, D>(props: VirtualMasonryProps<T, D>) {
  const { items, keyOf, ratioOf, card, data, columns, root, label, gap = 4 } = props;
  const count = Math.max(1, Math.floor(columns));
  const [grid, attach, width] = useGrid();
  const view = useView(root, grid);
  const columnWidth = width !== null && width > 0 ? (width - gap * (count - 1)) / count : 0;
  const ratios = useMemo(() => items.map(ratioOf), [items, ratioOf]);
  const lanes = useMemo(() => placeCards(ratios, count, columnWidth, gap), [ratios, count, columnWidth, gap]);
  // A new width (the projects' panel opening or closing) scales every card's height: the scroll
  // scales with them, so the cards in view stay in view rather than the view jumping (issue 784).
  const shownWidth = useRef(columnWidth);
  useEffect(() => {
    const before = shownWidth.current;
    shownWidth.current = columnWidth;
    if (root === null || before <= 0 || columnWidth <= 0 || before === columnWidth) return;
    root.scrollTo({ top: root.scrollTop * (columnWidth / before) });
  }, [root, columnWidth]);
  const flow = width === 0 || view === NO_LAYOUT;
  const placed = !flow && columnWidth > 0 && view !== null ? view : null;
  return (
    <div
      ref={attach}
      className={classes.grid}
      style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gap }}
      role={label === undefined ? undefined : "group"}
      aria-label={label}
    >
      {lanes.map((lane, column) => (
        <div
          key={column}
          className={placed === null ? classes.column : `${classes.column} ${own.placedColumn}`}
          style={
            placed === null
              ? { animationDelay: `${column * 100}ms`, gap }
              : { animationDelay: `${column * 100}ms`, height: lane.height }
          }
        >
          {lane.cards.map((place) => {
            const item = items[place.index]!;
            if (flow) return <Slot key={keyOf(item)} card={card} item={item} data={data} />;
            if (placed === null) return null;
            const near = place.top + place.height >= placed.top && place.top <= placed.bottom;
            return (
              <div
                key={keyOf(item)}
                className={near ? own.placed : `${own.placed} ${own.tile}`}
                style={{ top: place.top, height: place.height }}
                aria-hidden={near ? undefined : true}
              >
                {near ? <Slot card={card} item={item} data={data} /> : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
